"use client";

import { useEffect, useState, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export default function ThreeViewer({ controlsRef, isFullscreen, modelUrl }) {
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Keep instances alive between renders
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsInstance = useRef(null);

/*   const modelURL = modelUrl || "/test-threejs/piante_con_extras.gltf";
 */

  const modelURL = modelUrl;
  
  // --------------------------------------------------
  // Helpers for fullscreen dimensions
  // --------------------------------------------------
  const getWidth = () =>
    typeof window !== "undefined" && isFullscreen ? window.innerWidth : 750;

  const getHeight = () =>
    typeof window !== "undefined" && isFullscreen ? window.innerHeight : 400;

  const width = getWidth();
  const height = getHeight();
  const initialSizeRef = useRef({ width, height });

  // --------------------------------------------------
  // 2. Handle resize when fullscreen toggles
  // --------------------------------------------------
  useEffect(() => {
    if (!cameraRef.current || !rendererRef.current) return;

    const camera = cameraRef.current;
    const renderer = rendererRef.current;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  }, [width, height]);

  // --------------------------------------------------
  // 3. Initialize scene ONCE
  // --------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;

    const { width: initialWidth, height: initialHeight } = initialSizeRef.current;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75,
      initialWidth / initialHeight,
      0.1,
      1000,
    );
    camera.position.set(0, 5, 3);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(initialWidth, initialHeight);
    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controlsInstance.current = controls;

    // Expose zoom controls to parent
    if (controlsRef) {
      controlsRef.current = {
        zoomOut: () => {
          const dir = new THREE.Vector3()
            .subVectors(camera.position, controls.target)
            .multiplyScalar(0.8);
          camera.position.copy(controls.target).add(dir);
        },
        zoomIn: () => {
          const dir = new THREE.Vector3()
            .subVectors(camera.position, controls.target)
            .multiplyScalar(1.25);
          camera.position.copy(controls.target).add(dir);
        },
      };
    }

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const dl = new THREE.DirectionalLight(0xffffff, 1.5);
    dl.position.set(5, 10, 7.5);
    scene.add(dl);

    let frameId;
    function animate() {
      frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    }

    animate();

    return () => {
      cancelAnimationFrame(frameId);
      renderer.dispose();
      controls.dispose();
      scene.clear();
    };
  }, [controlsRef]);

  // --------------------------------------------------
  // 4. Load model when API URL arrives
  // --------------------------------------------------
  useEffect(() => {
    if (!modelURL || !sceneRef.current) return;

    const scene = sceneRef.current;
    let isCurrent = true;
    setLoading(true);

    // Remove previous model if exists
    const prevModel = scene.getObjectByName("loadedModel");
    if (prevModel) {
      scene.remove(prevModel);
      prevModel.traverse((c) => {
        if (c.geometry) c.geometry.dispose();
        if (c.material) {
          if (Array.isArray(c.material)) {
            c.material.forEach((m) => m.dispose());
          } else {
            c.material.dispose();
          }
        }
      });
    }

    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("/draco/gltf/");
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      modelURL,
      (gltf) => {
        if (!isCurrent) return;
        const model = gltf.scene;
        model.name = "loadedModel";
        scene.add(model);

        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDimension = Math.max(size.x, size.y, size.z);

        if (Number.isFinite(maxDimension) && maxDimension > 0) {
          model.position.sub(center);

          const camera = cameraRef.current;
          const controls = controlsInstance.current;
          const distance = maxDimension * 1.8;

          camera.position.set(distance, distance, distance);
          camera.near = Math.max(maxDimension / 1000, 0.01);
          camera.far = Math.max(maxDimension * 100, 1000);
          camera.updateProjectionMatrix();

          controls.target.set(0, 0, 0);
          controls.update();
        }

        setLoading(false);
      },
      undefined,
      (err) => {
        if (!isCurrent) return;
        console.error("GLTF Load Error:", err);
        setLoading(false);
      },
    );

    return () => {
      isCurrent = false;
      dracoLoader.dispose();
    };
  }, [modelURL]);

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {loading && <p>Loading model...</p>}
      <div ref={containerRef} style={{ width, height }} className="block" />
    </div>
  );
}
